import React from "react";
import Checkbox from "../../../components/Checkbox";

import useMediaQuery from "../../../hooks/useMediaQuery";
import { ESTADO_SOBRA_LABEL, ESTADO_SOBRA_TONE } from "../../../services/suprimentos";
import { mq } from "../../../styles/canonical/breakpoints";
import { nomeMaterialDaSobra } from "../../../utils/nomeMaterial";
import { fmtData } from "../helpers";
import { CardItem, CardLista, TapCheck } from "../_shared/quadro/styles";
import { Pill, Table, TableWrapper, TruncCell } from "../styles";
import { Medida } from "./styles";

export function fmtMm(v) {
  if (v === null || v === undefined || v === "") return "—";
  return Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

function medidasDe(s) {
  return `${fmtMm(s.comprimento_mm)} × ${fmtMm(s.largura_mm)}${s.espessura_mm ? ` × ${fmtMm(s.espessura_mm)}` : ""}`;
}

/**
 * Cards no celular (auditoria mobile 2026-09-24, S2): oito colunas em 307px
 * viravam "S…", "35…", "Dis". A medida é a manchete — é o que o almoxarife
 * procura numa sobra — com material e código embaixo, box e m² como
 * metadado e o estado (ou a referência) como selo.
 */
function CardsSobras({ sobras, selecionadas, onToggleSel, onToggleTodos, onAbrir, colRef }) {
  const nSel = sobras.filter((s) => selecionadas.has(s.id)).length;
  const todas = sobras.length > 0 && nSel === sobras.length;
  return (
    <CardLista data-testid="sobras-cards">
      {onToggleTodos ? (
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, color: "var(--ink-2)", padding: "0 0 2px 11px" }}>
          <TapCheck>
            <Checkbox
              aria-label={todas ? "Desmarcar todas as sobras" : "Selecionar todas as sobras"}
              checked={todas}
              ref={(el) => { if (el) el.indeterminate = nSel > 0 && !todas; }}
              onChange={() => onToggleTodos(todas ? [] : sobras.map((s) => s.id))}
            />
          </TapCheck>
          {todas ? "Desmarcar todas" : "Selecionar todas"} ({sobras.length})
        </label>
      ) : null}
      {sobras.map((s) => {
        const sel = selecionadas.has(s.id);
        return (
          <CardItem
            key={s.id}
            $sel={sel}
            role="button"
            tabIndex={0}
            onClick={() => onAbrir(s.id)}
            onKeyDown={(e) => { if (e.key === "Enter") onAbrir(s.id); }}
          >
            <div className="linha">
              <TapCheck onClick={(e) => e.stopPropagation()}>
                <Checkbox aria-label={`Selecionar ${s.codigo}`} checked={sel} onChange={() => onToggleSel(s.id)} />
              </TapCheck>
              <span className="sub cresce" style={{ fontFamily: "var(--font-mono)", color: "var(--ink-2)" }}>{s.codigo}</span>
              <span className="fim">
                {colRef ? null : (
                  <Pill $tone={ESTADO_SOBRA_TONE[s.estado] || "neutral"}>
                    {ESTADO_SOBRA_LABEL[s.estado] || s.estado_display || s.estado}
                  </Pill>
                )}
              </span>
            </div>
            <div className="manchete"><Medida>{medidasDe(s)} mm</Medida></div>
            <div className="titulo" style={{ fontSize: 14 }} title={`${nomeMaterialDaSobra(s)} (${s.produto_codigo})`}>{nomeMaterialDaSobra(s)}</div>
            <div className="sub">
              {Number(s.area_m2 || 0).toFixed(3)} m² · Box {s.box_codigo || "—"}
              {colRef
                ? ` · ${colRef.titulo.toLowerCase()} ${s[colRef.campo] || "—"} · ${fmtData(s[colRef.quando] || s.updated_at)}`
                : ` · criada ${fmtData(s.created_at)}`}
            </div>
          </CardItem>
        );
      })}
    </CardLista>
  );
}

/** Zero scroll lateral: `table-layout: fixed` + ellipsis (constitucional). */
/**
 * `referencia`: nas abas Reservadas/Usadas a coluna Estado (redundante) dá
 * lugar a "Reservada por" / "Usada em" — o que o almoxarife quer ver ali.
 */
export default function TabelaSobras({ sobras, selecionadas, onToggleSel, onToggleTodos, onAbrir, referencia = null }) {
  const mobile = useMediaQuery(mq.mobile);
  const nSel = sobras.filter((s) => selecionadas.has(s.id)).length;
  const todas = sobras.length > 0 && nSel === sobras.length;
  const parcial = nSel > 0 && !todas;
  const colRef = referencia === "reservada"
    ? { titulo: "Reservada por", campo: "reservada_por_ref", quando: "reservada_em" }
    : referencia === "usada"
      ? { titulo: "Usada em", campo: "usada_por_ref", quando: "usada_em" }
      : null;
  if (mobile) {
    return (
      <CardsSobras
        sobras={sobras}
        selecionadas={selecionadas}
        onToggleSel={onToggleSel}
        onToggleTodos={onToggleTodos}
        onAbrir={onAbrir}
        colRef={colRef}
      />
    );
  }
  return (
    <TableWrapper>
      <Table>
        <colgroup>
          <col style={{ width: "4%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: colRef ? "26%" : "30%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: colRef ? "16%" : "12%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>
              <Checkbox
                aria-label={todas ? "Desmarcar todas as sobras" : "Selecionar todas as sobras"}
                checked={todas}
                ref={(el) => { if (el) el.indeterminate = parcial; }}
                onChange={() => onToggleTodos?.(todas ? [] : sobras.map((s) => s.id))}
                disabled={!onToggleTodos || sobras.length === 0}
              />
            </th>
            <th>Código</th>
            <th>Material</th>
            <th>Medidas (mm)</th>
            <th>m²</th>
            <th>Box</th>
            <th>{colRef ? colRef.titulo : "Estado"}</th>
            <th>{colRef ? "Quando" : "Criada"}</th>
          </tr>
        </thead>
        <tbody>
          {sobras.map((s) => (
            <tr key={s.id} style={{ cursor: "pointer" }} onClick={() => onAbrir(s.id)}>
              <td onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  aria-label={`Selecionar ${s.codigo}`}
                  checked={selecionadas.has(s.id)}
                  onChange={() => onToggleSel(s.id)}
                />
              </td>
              <td><TruncCell>{s.codigo}</TruncCell></td>
              <td title={`${nomeMaterialDaSobra(s)} (${s.produto_codigo})`}>
                <TruncCell>{nomeMaterialDaSobra(s)}</TruncCell>
              </td>
              <td>
                <Medida>{medidasDe(s)}</Medida>
              </td>
              <td><Medida>{Number(s.area_m2 || 0).toFixed(3)}</Medida></td>
              <td><TruncCell>{s.box_codigo || "—"}</TruncCell></td>
              {colRef ? (
                <>
                  <td title={s[colRef.campo] || ""}><TruncCell>{s[colRef.campo] || "—"}</TruncCell></td>
                  <td>{fmtData(s[colRef.quando] || s.updated_at)}</td>
                </>
              ) : (
                <>
                  <td>
                    <Pill $tone={ESTADO_SOBRA_TONE[s.estado] || "neutral"}>
                      {ESTADO_SOBRA_LABEL[s.estado] || s.estado_display || s.estado}
                    </Pill>
                  </td>
                  <td>{fmtData(s.created_at)}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
}
